import { keepPreviousData, useQuery } from '@tanstack/react-query';

import * as TourOffersService from '~/services/TourOffersService';

export const tourOfferQueryKeys = {
  all: ['tour-offers'] as const,
  lists: () => [...tourOfferQueryKeys.all, 'list'] as const,
  list: (search: string, ltleOnly: boolean) => [...tourOfferQueryKeys.lists(), search, ltleOnly] as const,
  details: () => [...tourOfferQueryKeys.all, 'detail'] as const,
  detail: (tourOfferId: string | undefined) => [...tourOfferQueryKeys.details(), tourOfferId ?? ''] as const,
  departures: (tourOfferId: string | undefined) =>
    [...tourOfferQueryKeys.all, 'departures', tourOfferId ?? ''] as const,
};

/** Callers debounce, so each settled term is its own cache entry. */
export function useSearchTourOffers(search: string, ltleOnly: boolean) {
  return useQuery({
    queryKey: tourOfferQueryKeys.list(search, ltleOnly),
    queryFn: ({ signal }) => TourOffersService.searchTourOffers({ search: search || undefined, ltleOnly }, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** There is no single-offer summary endpoint; searching by id matches exactly. */
export function useGetTourOfferSummary(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.detail(tourOfferId),
    queryFn: async ({ signal }) => {
      const offers = await TourOffersService.searchTourOffers({ search: tourOfferId }, { signal });
      return offers.find((offer) => offer.id === tourOfferId) ?? null;
    },
    enabled: !!tourOfferId,
  });
}

export function useGetTourOfferDepartures(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.departures(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOfferDepartures(tourOfferId, { signal }),
    enabled: !!tourOfferId,
  });
}
